import React, { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import SearchIcon from '@mui/icons-material/Search';
// Theme icons are now handled by the theme system

import './Navbar.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import urls from '../../urls';
import axiosWrapper from '../../utils/apiRequests/axiosWrapper';
import extractAuthFromApiBody from '../../utils/extractAuthFromApiBody';
import { logout, updateUserInfo } from '../../features/user/userSlice';
import { hydrateUserData, clearUserCache } from '../../utils/cache/dataHydration';
import cacheSync from '../../utils/cache/cacheSync';
import { useTheme } from '../../contexts/ThemeContext';
import useThemeStyles from '../../hooks/useThemeStyles';
import useThemeToggle from '../../hooks/useThemeToggle';
import { useCommandPalette, SHORTCUT_LABEL } from '../CommandPalette';

import LoginModal from '../Login/LoginModal';
import RegisterModal from '../Register/RegisterModal';
import logo from '../../assets/Logo.svg';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);
  const { getThemeMetadata } = useTheme();
  const { components, colors } = useThemeStyles();
  const handleThemeToggle = useThemeToggle();
  const { open: openCommandPalette } = useCommandPalette();

  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);

  // Check if we're on the home page
  const isHomePage = location.pathname === urls.base;

  const onTitleClick = () => {
    // Navigate to dashboard if logged in, otherwise landing page
    if (user?._id) {
      navigate(urls.dashboard);
    } else {
      navigate(urls.base);
    }
  };

  useEffect(() => {
    // Extract token from userInfo if it exists (for backward compatibility)
    if (user?._id && !localStorage.getItem('authToken')) {
      const token = user?.token || user?.accessToken || user?.access_token || user?.authToken;
      if (token) {
        localStorage.setItem('authToken', token);
      }
    }

    // Only fetch user data if we don't already have a user
    if (user?._id) {
      return;
    }
    const fetchData = async () => {
      const data = await axiosWrapper({
        method: 'get',
        path: 'users/loggedin',
        optimistic: false,
      });

      const { user: sessionUser, token } = extractAuthFromApiBody(data?.data);
      if (sessionUser?._id) {
        if (token) {
          localStorage.setItem('authToken', token);
        }
        dispatch(updateUserInfo(sessionUser));

        // Hydrate user data into cache after fetching user info
        const userId = sessionUser._id;
        if (userId) {
          hydrateUserData(userId).catch((err) => {
            console.error('[Navbar] Error hydrating user data:', err);
          });
        }
      }
    };

    fetchData();

    // Initialize cache sync for reconnection handling
    cacheSync.init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogOut = async () => {
    const data = await axiosWrapper({
      method: 'get',
      path: 'users/logout',
      optimistic: false,
    });

    if (data) {
      // Clear token from localStorage
      localStorage.removeItem('authToken');

      // Clear all cached data on logout
      clearUserCache().catch((err) => {
        console.error('[Navbar] Error clearing cache on logout:', err);
      });

      // Cleanup cache sync listeners
      cacheSync.cleanup();

      navigate('/');
      dispatch(logout());
    }
  };

  // Clean styling using theme system - no ternaries or magic strings
  const navbarStyle = components.navbar;
  const buttonStyle = {
    ...components.button.primary,
    marginLeft: '8px',
  };
  const buttonHoverStyle = components.button.hover;

  return (
    <AppBar position="static" className="Navbar" style={navbarStyle}>
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, cursor: 'pointer' }}
          className="MuiTypography-root"
          onClick={onTitleClick}
          style={{ color: colors.text.primary }}
        >
          <img src={logo} alt="Logo" style={{ height: '72px', marginRight: '8px' }} />
          PlotWyze
        </Typography>

        {/* Quick search / command palette (logged-in users) */}
        {user?._id && (
          <ButtonBase
            onClick={openCommandPalette}
            aria-label={`Search everything (${SHORTCUT_LABEL})`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              mr: 1.5,
              borderRadius: '8px',
              border: `1px solid ${colors.border.subtle}`,
              backgroundColor: colors.background.secondary,
              color: colors.text.secondary,
              fontSize: '0.8125rem',
              '&:hover': {
                borderColor: colors.interactive.primary,
                color: colors.text.primary,
              },
            }}
          >
            <SearchIcon sx={{ fontSize: '1rem' }} />
            <span>Search</span>
            <Typography
              component="kbd"
              sx={{
                fontFamily: 'inherit',
                fontSize: '0.6875rem',
                lineHeight: 1,
                px: 0.6,
                py: 0.4,
                borderRadius: '4px',
                border: `1px solid ${colors.border.subtle}`,
                color: colors.text.muted,
              }}
            >
              {SHORTCUT_LABEL}
            </Typography>
          </ButtonBase>
        )}

        {/* Theme Toggle Button */}
        <IconButton
          onClick={handleThemeToggle}
          style={{
            color: colors.text.primary,
            marginRight: '8px',
          }}
          aria-label={`Switch to ${getThemeMetadata().name} theme`}
        >
          {getThemeMetadata().icon}
        </IconButton>

        {/* Settings Button - shown when logged in and not on home page */}
        {user?._id && !isHomePage && (
          <Button
            className="MuiButton-root"
            onClick={() => navigate(urls.settings)}
            style={buttonStyle}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = buttonHoverStyle.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = buttonStyle.backgroundColor;
            }}
          >
            Settings
          </Button>
        )}

        {user?._id && isHomePage && (
          <Button
            className="MuiButton-root"
            onClick={() => navigate(urls.dashboard)}
            style={buttonStyle}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = buttonHoverStyle.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = buttonStyle.backgroundColor;
            }}
          >
            Enter
          </Button>
        )}
        {user?._id && !isHomePage && (
          <Button
            className="MuiButton-root"
            onClick={handleLogOut}
            style={buttonStyle}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = buttonHoverStyle.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = buttonStyle.backgroundColor;
            }}
          >
            Logout
          </Button>
        )}
        {!user?._id && (
          <>
            <Button
              className="MuiButton-root"
              onClick={() => setIsLoginOpen(true)}
              style={buttonStyle}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = buttonHoverStyle.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = buttonStyle.backgroundColor;
              }}
            >
              Login
            </Button>
            <Button
              className="MuiButton-root"
              onClick={() => setIsRegisterOpen(true)}
              style={buttonStyle}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = buttonHoverStyle.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = buttonStyle.backgroundColor;
              }}
            >
              Register
            </Button>
          </>
        )}
        {isLoginOpen && (
          <LoginModal
            onClose={() => setIsLoginOpen(false)}
            switchToRegister={() => {
              setIsRegisterOpen(true);
              setIsLoginOpen(false);
            }}
          />
        )}
        {isRegisterOpen && (
          <RegisterModal
            onClose={() => setIsRegisterOpen(false)}
            switchToLogin={() => {
              setIsRegisterOpen(false);
              setIsLoginOpen(true);
            }}
          />
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
